import { useMemo } from 'react';
import { NumberField, Label, FieldError } from '@heroui/react';
import {
  FieldValues,
  Path,
  useController,
  UseControllerProps
} from 'react-hook-form';

import { useGlobalComponent } from '@rhino-project/core/hooks';
import { stripRhinoProps } from './utils/stripRhinoProps';

export type FieldNumberInputProps<T extends FieldValues = FieldValues> = Omit<
  UseControllerProps<T>,
  'name' | 'control'
> & {
  accessor?: (value: unknown) => number | null | undefined;
  path: Path<T>;
  label?: string;
  isRequired?: boolean;
  isClearable?: boolean;
  onClear?: () => void;
  className?: string;
  [key: string]: unknown;
};

export const FieldNumberInputBase = <T extends FieldValues = FieldValues>({
  accessor,
  path,
  rules,
  shouldUnregister,
  defaultValue,
  disabled: propDisabled,
  label,
  isRequired,
  className,
  ...props
}: FieldNumberInputProps<T>) => {
  const {
    field: { disabled, onChange, value: fieldValue, ...fieldProps },
    fieldState: { error }
  } = useController({
    name: path,
    rules,
    shouldUnregister,
    defaultValue,
    disabled: propDisabled
  });

  const value = useMemo(
    () => (accessor ? accessor(fieldValue) : fieldValue),
    [accessor, fieldValue]
  );

  return (
    <NumberField
      {...fieldProps}
      isInvalid={!!error}
      isDisabled={disabled}
      isRequired={isRequired}
      value={value ?? NaN}
      onChange={(val) => onChange(val)}
      className={className}
      {...stripRhinoProps(props)}
    >
      {label && <Label>{label}</Label>}
      <NumberField.Group>
        <NumberField.DecrementButton />
        <NumberField.Input />
        <NumberField.IncrementButton />
      </NumberField.Group>
      <FieldError>{error?.message}</FieldError>
    </NumberField>
  );
};

export const FieldNumberInput = (props: FieldNumberInputProps) =>
  useGlobalComponent('FieldNumberInput', FieldNumberInputBase, props);
